import React, { useState } from 'react';
import Editor from './Editor';
import { connect } from 'react-redux';
import { getEntityById, nullSafeGet } from '../../utils/DataAccessUtils';
import { PageLoadingPlaceholder } from '../page_loading_placeholder/PageLoadingPlaceholder';
import { Button, Col, Dropdown, List, Menu, Popconfirm, Row } from 'antd';
import { DeleteOutlined, PlusOutlined } from '@ant-design/icons';
import AIIcon from '../AIIcon';
import {
	locationsRestCrudThunksForBuyer,
	locationsRestCrudThunksForSupplier,
} from '../../thunks/locations_thunks';
import { ROLE_TYPES } from '../../utils/DataConstants';
import { withUserType } from '../withUserType';
import { withCurrentUser } from '../../hooks/withCurrentUser';
import TemplateSelector from './Templates';
import { EmptyState } from '../empty_state/EmptyState';
import AIGenerateDocument from './AIGenerateDocument';
import { Document, Documents, LocationDocumentsProps } from '../../types/documentTypes';
import { isBuyerAllowedToAccess, PERMISSION_NAMES } from '../../utils/AuthUtils';
import posthog from 'posthog-js';
import AccessPermissionChecker from '../common/AccessPermissionChecker';

const LocationDocuments: React.FC<LocationDocumentsProps> = ({
	userLocation,
	updateDocument,
	companyConfig,
	currentUser,
}) => {
	const [selectedDocumentIndex, setSelectedDocumentIndex] = useState(0);
	const [showTemplates, setShowTemplates] = useState(false);
	const [showAIDocument, setShowAIDocument] = useState(false);

	const documents: Documents = nullSafeGet('documents', userLocation)
		? Array.isArray(userLocation.documents)
			? userLocation.documents
			: [userLocation.documents]
		: nullSafeGet('documents.blocks', userLocation)
		? [{ blocks: userLocation.documents.blocks }]
		: [];

	const deleteDocument = (index: number) => {
		const newDocuments = [...documents];
		newDocuments.splice(index, 1);
		updateDocument(userLocation.id, {
			documents: newDocuments,
		});
	};

	const addBlankDocument = () => {
		const newDocument: Document = { title: '', blocks: '' };
		updateDocument(userLocation.id, {
			documents: [...documents, newDocument],
		}).then(() => {
			setSelectedDocumentIndex(documents.length);
		});
	};

	if (!nullSafeGet('id', userLocation)) return <PageLoadingPlaceholder />;

	const selectedDocument: Document = nullSafeGet(`${selectedDocumentIndex}`, documents);

	const canEditDocuments = isBuyerAllowedToAccess(
		PERMISSION_NAMES.MODIFY_LOCATION_DOCUMENTS,
		companyConfig,
		nullSafeGet('roles', currentUser)
	);

	return (
		<Row className="h-full min-h-[70vh] bg-white">
			<Col span={8} style={{ borderRight: '1px solid #E5E5E5' }}>
				<div
					className="flex flex-row items-center justify-between px-6 py-4"
					style={{ borderBottom: '1px solid #E5E5E5' }}
				>
					<div className="h5medium">Documents ({documents.length})</div>
					<AccessPermissionChecker name={PERMISSION_NAMES.MODIFY_LOCATION_DOCUMENTS}>
						<div className="flex flex-row items-center justify-end">
							<Dropdown
								className="ml-4"
								overlay={
									<Menu>
										<Menu.Item key="pdf" onClick={addBlankDocument}>
											Start Blank
										</Menu.Item>
										<Menu.Item key="template" onClick={() => setShowTemplates(true)}>
											Use Template
										</Menu.Item>
										{posthog.isFeatureEnabled('wiki-withai') ? (
											<Menu.Item key="ai" icon={<AIIcon />} onClick={() => setShowAIDocument(true)}>
												Start with AI
											</Menu.Item>
										) : null}
									</Menu>
								}
								placement="bottomRight"
								trigger={['click']}
							>
								<Button type="primary" icon={<PlusOutlined />}>
									New
								</Button>
							</Dropdown>
						</div>
					</AccessPermissionChecker>
				</div>
				{documents.length > 0 ? (
					<div className="flex flex-col">
						<List
							dataSource={documents}
							renderItem={(document: Document, index: number) => (
								<List.Item
									className={` group cursor-pointer bg-neutral-1 px-6 py-1 hover:bg-neutral-2 ${
										selectedDocumentIndex === index ? 'bg-neutral-3' : ''
									}`}
									key={index}
									onClick={() => setSelectedDocumentIndex(index)}
								>
									<div className="flex w-full flex-row items-baseline justify-between px-6 py-1">
										<span className="bodyregular">{document.title || 'New Document'}</span>
										<div className="relative">
											<Popconfirm
												title="Are you sure you want to delete this document?"
												onConfirm={() => deleteDocument(index)}
											>
												<Button
													type="text"
													icon={<DeleteOutlined className="stroke-black" />}
													className=" opacity-0 transition-opacity duration-200 group-hover:opacity-100"
												/>
											</Popconfirm>
										</div>
									</div>
								</List.Item>
							)}
						/>
					</div>
				) : (
					<div className="flex h-full items-center justify-center">
						<EmptyState
							body={<span className="bodyregular">You Haven’t Created Any Documents</span>}
						/>
					</div>
				)}
			</Col>
			<Col span={16}>
				{documents.length > 0 ? (
					<Editor
						key={selectedDocumentIndex}
						document={selectedDocument}
						updateDocument={(documentData: Document) => {
							return updateDocument(userLocation.id, {
								documents: [
									...documents.slice(0, selectedDocumentIndex),
									documentData,
									...documents.slice(selectedDocumentIndex + 1),
								],
							});
						}}
						editable={canEditDocuments}
					/>
				) : (
					<div className="flex h-full items-center justify-center">
						<EmptyState
							body={
								<span className="h4medium inline-block max-w-[440px] text-center">
									Keep important files, procedures, and location details organized by creating your
									first document.
								</span>
							}
						/>
					</div>
				)}
			</Col>
			{showTemplates ? (
				<TemplateSelector
					onSelectTemplate={(template: Document) => {
						updateDocument(userLocation.id, {
							documents: [...documents, template],
						}).then(() => {
							setShowTemplates(false);
							setSelectedDocumentIndex(documents.length);
						});
					}}
					onCancel={() => setShowTemplates(false)}
				/>
			) : null}
			{showAIDocument ? (
				<AIGenerateDocument
					onGenerateDocument={(template: Document) => {
						updateDocument(userLocation.id, {
							documents: [...documents, template],
						}).then(() => {
							setShowAIDocument(false);
							setSelectedDocumentIndex(documents.length);
						});
					}}
					onCancel={() => setShowAIDocument(false)}
				/>
			) : null}
		</Row>
	);
};

const mapStateToProps = (state, ownProps) => ({
	userLocation: getEntityById(ownProps.match.params.id, state.locations),
	companyConfig: state.company_config.detail,
	currentUser: state.session.currentUser,
});

const mapDispatchToProps = (dispatch, ownProps) => ({
	updateDocument: (id, entity) =>
		dispatch(
			ownProps.userType === ROLE_TYPES.SUPPLIER
				? locationsRestCrudThunksForSupplier.patch(id, entity)
				: locationsRestCrudThunksForBuyer.patch(id, entity)
		),
});

export default withUserType(
	withCurrentUser(connect(mapStateToProps, mapDispatchToProps)(LocationDocuments))
);
